import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const values = [
  {
    icon: "ri-eye-line",
    title: "内容发现高效",
    desc: "清晰的频道分类与智能推荐，帮你在海量直播中快速找到想看的内容。",
  },
  {
    icon: "ri-hd-line",
    title: "画质体验优先",
    desc: "坚持高清、低延迟的观看体验，让每一场直播都清晰流畅。",
  },
  {
    icon: "ri-heart-3-line",
    title: "互动氛围温暖",
    desc: "弹幕与社区让观看不再孤单，与志同道合的观众一起聊直播。",
  },
  {
    icon: "ri-shield-check-line",
    title: "健康内容生态",
    desc: "倡导绿色直播，营造积极、友善、多元的内容社区氛围。",
  },
];

const timeline = [
  { year: "2021", title: "平台诞生", desc: "360直播正式上线，首批开放游戏与娱乐两大频道。" },
  { year: "2023", title: "体育频道上线", desc: "引入赛事解说与多机位直播，覆盖足球、篮球等热门项目。" },
  { year: "2024", title: "互动社区发布", desc: "上线话题社区与弹幕互动，观众从看直播走向聊直播。" },
  { year: "2026", title: "多频道升级", desc: "频道数突破 3200，日活观众达 1200 万，持续优化观看体验。" },
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 360直播 | 多频道高清视频直播平台",
    "了解360直播平台的定位、发展理念与运营团队，以及联系方式与反馈渠道。"
  );

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-8 md:px-6 md:pt-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      {/* Hero */}
      <section className="mx-auto mt-6 w-full max-w-[1440px] px-4 md:px-6">
        <div className="relative overflow-hidden rounded-xl border border-background-200/60">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20broadcast%20control%20room%20with%20multiple%20glowing%20screens%20and%20azure%20UI%20panels%2C%20dark%20futuristic%20technology%20atmosphere%2C%20cinematic%20wide%20shot%2C%20highly%20detailed&width=1600&height=700&seq=about-hero-360&orientation=landscape"
            alt="360直播 平台团队"
            title="关于360直播"
            className="h-64 w-full object-cover object-top md:h-80"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs font-label font-semibold text-primary-300">
              <i className="ri-live-line" />
              关于 360直播
            </span>
            <h1 className="mt-4 max-w-2xl font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
              让每一个人都能轻松享受高清直播
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              360直播是一个多频道高清视频直播平台，聚合游戏、娱乐、体育等丰富内容，致力于用更清晰、更流畅、更有温度的方式，陪伴你的每一段观看时光。
            </p>
          </div>
        </div>
      </section>

      {/* Intro + values */}
      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-label font-semibold uppercase tracking-[0.2em] text-primary-400">
              <span className="h-3 w-1 rounded-full bg-primary-500" />
              Who We Are
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              平台简介与核心理念
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              360直播自 2021 年成立以来，始终围绕“高清、易发现、可互动”三个关键词打磨产品体验。我们相信，好的直播平台不只是播放视频，更应该帮助观众快速发现内容、找到同好。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              目前平台已覆盖游戏、娱乐、体育三大核心频道，支持蓝光高清画质与实时弹幕互动，并不断推进真实播放与互动能力的建设。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/channels"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-live-line" />
                浏览直播频道
              </Link>
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
              >
                <i className="ri-feedback-line" />
                提交反馈
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
            {values.map((value) => (
              <div
                key={value.title}
                className="rounded-lg border border-background-200/60 bg-background-100 p-5 transition-colors hover:border-primary-500/50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className={`${value.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full bg-background-100/50">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8">
            <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-label font-semibold uppercase tracking-[0.2em] text-primary-400">
              <span className="h-3 w-1 rounded-full bg-primary-500" />
              Milestones
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              发展历程
            </h2>
            <p className="mt-3 text-sm text-foreground-600">一步一个脚印，从直播到社区，不断成长。</p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {timeline.map((item, index) => (
              <div key={item.year} className="relative rounded-lg border border-background-200/60 bg-background-100 p-5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 font-label text-xs font-bold text-background-50">
                  {index + 1}
                </span>
                <p className="mt-4 font-heading text-lg font-extrabold text-primary-300">{item.year}</p>
                <h3 className="mt-1 font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 rounded-xl border border-background-200/60 bg-background-100 p-6 md:p-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是合作洽谈、内容建议还是问题反馈，都欢迎随时联系我们，我们会尽快回复。
            </p>
            <div className="mt-6 flex flex-col gap-4 text-sm">
              <div className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className="ri-mail-line" />
                </span>
                support@360zhibo.com
              </div>
              <div className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className="ri-customer-service-2-line" />
                </span>
                在线客服 · 每日 09:00 - 22:00
              </div>
              <div className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className="ri-map-pin-line" />
                </span>
                上海市浦东新区数字创意园 8 号楼
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { label: "合作洽谈", desc: "内容合作、品牌活动、赛事转播", icon: "ri-shake-hands-line" },
                { label: "内容建议", desc: "推荐你想看的直播与频道", icon: "ri-lightbulb-line" },
                { label: "问题反馈", desc: "使用中的问题与体验建议", icon: "ri-customer-service-line" },
                { label: "加入我们", desc: "主播入驻与团队招聘", icon: "ri-user-add-line" },
              ].map((card) => (
                <Link
                  key={card.label}
                  to="/feedback"
                  className="rounded-lg border border-background-200/60 bg-background-50 p-5 transition-colors hover:border-primary-500/60"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                    <i className={`${card.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                    {card.label}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-600">{card.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
          <Link
            to="/channels"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-live-line" />
            浏览频道分类
          </Link>
        </div>
      </section>
    </div>
  );
}